/**
 * REACTIONS, GROUPED FOR A FEED ENTRY — migration 0143.
 *
 * The store holds one flat cross-board list of reactions (every board the person
 * can open). A feed entry needs only its own, grouped by emoji with a count and
 * whether the signed-in person is in each group — which is what the bar draws.
 *
 * The set is small and fixed on purpose. A reaction is a quick, quiet
 * acknowledgement, and a short curated row reads as a professional tool rather
 * than a chat toy (emoji were taken out of the icon set once for looking cheap —
 * these stay few, and the bar stays muted). Order here is the order the palette
 * offers them, so a card reads the same everywhere.
 */
import type { Reaction } from './types';

/**
 * The offered reactions, in the order the palette shows them. Chosen for work
 * cards a team leaves status updates and notes on, not a chat: acknowledge, thank
 * and praise, then the arc of the work itself — reviewed, done, shipped, landed —
 * and one flag to pull a reader's eye to something that needs attention.
 *   👍 acknowledged   🙏 thanks     🙌 nice work   ✅ done / verified
 *   👀 reviewing      🚀 shipped    🎉 milestone    🚩 flag for attention
 */
export const REACTION_SET = ['👍', '🙏', '🙌', '✅', '👀', '🚀', '🎉', '🚩'] as const;

export type ReactionGroup = {
  emoji: string;
  count: number;
  /** Whether the signed-in person has left this one. */
  mine: boolean;
  /** The addresses that left it, oldest first — for the hover that names them. */
  reactors: string[];
};

/**
 * Every reaction on one entry, grouped by emoji. Groups are ordered by when the
 * emoji was FIRST used on this entry, so the bar is stable as counts change (a
 * newly popular reaction does not jump the row). `me` is lower-cased.
 */
export function groupFor(all: Reaction[], targetId: string, me: string): ReactionGroup[] {
  const mine = me.toLowerCase();
  const byEmoji = new Map<string, Reaction[]>();
  for (const r of all) {
    if (r.targetId !== targetId) continue;
    const list = byEmoji.get(r.emoji);
    if (list) list.push(r);
    else byEmoji.set(r.emoji, [r]);
  }
  const groups: ReactionGroup[] = [];
  for (const [emoji, rs] of byEmoji) {
    const ordered = [...rs].sort((a, b) => a.at - b.at);
    groups.push({
      emoji,
      count: ordered.length,
      mine: ordered.some((r) => r.reactor.toLowerCase() === mine),
      reactors: ordered.map((r) => r.reactor),
      // the earliest `at` carried alongside for ordering below
    });
  }
  // Order groups by the earliest reaction in each, so the row does not reshuffle.
  const firstAt = (emoji: string) =>
    Math.min(...(byEmoji.get(emoji) ?? []).map((r) => r.at));
  return groups.sort((a, b) => firstAt(a.emoji) - firstAt(b.emoji));
}
